import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TABLE_CARD_THEME_CLS, TABLE_SHELL_CLS } from "@/lib/ads/tableTheme";
import { cn } from "@/lib/utils";

export type AdObjectDetailLevel = "campaign" | "adset" | "ad";

const PANEL_CLS = "h-full rounded-xl border-[#EAECF0] shadow-none";
const KPI_CARD_CLS = "rounded-2xl border border-[#EAECF0] bg-white px-5 py-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)]";

const KPI_LAYOUT: Record<AdObjectDetailLevel, { count: number; grid: string }> = {
  campaign: { count: 6, grid: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3" },
  adset: { count: 8, grid: "grid grid-cols-2 md:grid-cols-4 gap-3" },
  ad: { count: 10, grid: "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3" },
};

const LABELS: Record<AdObjectDetailLevel, string> = {
  campaign: "Loading campaign",
  adset: "Loading ad set",
  ad: "Loading ad",
};

function PanelRows({ count }: { count: number }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center justify-between gap-3 border-b border-[#F2F4F7] py-3 last:border-b-0">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-3.5 w-24" />
        </div>
      ))}
    </div>
  );
}

function DetailPanels() {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
      <Card className={cn(PANEL_CLS, "lg:col-span-2")}>
        <CardContent className="flex h-full flex-col p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Skeleton className="h-5 w-28" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-8 w-[140px] rounded-md" />
              <Skeleton className="h-8 w-[130px] rounded-md" />
            </div>
          </div>
          <Skeleton className="min-h-[220px] w-full flex-1 rounded-lg" />
        </CardContent>
      </Card>
      <Card className={PANEL_CLS}>
        <CardContent className="p-5">
          <Skeleton className="mb-3 h-5 w-36" />
          <PanelRows count={6} />
        </CardContent>
      </Card>
      <Card className={PANEL_CLS}>
        <CardContent className="p-5">
          <Skeleton className="mb-3 h-5 w-24" />
          <PanelRows count={5} />
        </CardContent>
      </Card>
    </div>
  );
}

function ChildTable() {
  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <Skeleton className="h-6 w-28" />
      </div>
      <Card className={cn(TABLE_SHELL_CLS, TABLE_CARD_THEME_CLS)}>
        <div className="flex items-center gap-6 border-b border-[#EAECF0] bg-muted/40 px-4 py-3.5">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="ml-auto h-3 w-16" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-12" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="flex items-center gap-6 border-b border-[#F2F4F7] px-4 py-4 last:border-b-0">
            <Skeleton className="h-4 w-4 rounded" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="ml-auto h-6 w-16 rounded-md" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </Card>
    </div>
  );
}

function AdPanels() {
  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <CardContent className="p-4">
          <Skeleton className="mb-3 h-8 w-full max-w-[400px] rounded-md" />
          <Skeleton className="h-[260px] w-full rounded-lg" />
        </CardContent>
      </Card>
      <Card className={PANEL_CLS}>
        <CardContent className="p-5">
          <Skeleton className="mb-3 h-5 w-28" />
          <div className="overflow-hidden rounded-xl border border-[#EAECF0] bg-white">
            <div className="flex items-center gap-2.5 px-3 py-2.5">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
            </div>
            <div className="space-y-1.5 px-3 pb-3">
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-3/4" />
            </div>
            <Skeleton className="h-[180px] w-full rounded-none" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function AdObjectDetailSkeleton({ level }: { level: AdObjectDetailLevel }) {
  const kpi = KPI_LAYOUT[level];
  return (
    <div className="p-6 space-y-6" role="status" aria-busy="true" aria-label={LABELS[level]} data-testid={`ad-object-detail-skeleton-${level}`}>
      <div
        className="flex items-center gap-1 rounded-lg px-2 py-1.5"
        style={{ background: "rgb(248, 250, 252)", border: "1px solid rgb(237, 241, 247)" }}
      >
        <Skeleton className="h-7 w-40 rounded-md" />
        <Skeleton className="mx-1 h-3.5 w-3.5 rounded" />
        <Skeleton className="h-7 w-36 rounded-md" />
        <Skeleton className="mx-1 h-3.5 w-3.5 rounded" />
        <Skeleton className="h-7 w-32 rounded-md" />
      </div>

      <div className="flex items-center gap-4">
        <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
        <div className="flex min-w-0 flex-1 items-center gap-3">
          {level === "ad" ? <Skeleton className="h-12 w-12 shrink-0 rounded-lg" /> : null}
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-7 w-64 max-w-full" />
              <Skeleton className="h-6 w-16 rounded-md" />
            </div>
            <Skeleton className="mt-2 h-4 w-36" />
          </div>
        </div>
        <Skeleton className={cn("h-9 shrink-0 rounded-lg", level === "ad" ? "w-32" : "w-40")} />
        <Skeleton className="h-9 w-9 shrink-0 rounded-md" />
      </div>

      <div className={kpi.grid}>
        {Array.from({ length: kpi.count }).map((_, i) => (
          <div key={i} className={KPI_CARD_CLS} data-testid="ad-object-detail-skeleton-kpi">
            <div className="flex items-center gap-2">
              <Skeleton className="h-4 w-4 rounded" />
              <Skeleton className="h-3.5 w-20" />
            </div>
            <Skeleton className="mt-3 h-[26px] w-24" />
          </div>
        ))}
      </div>

      {level === "ad" ? (
        <AdPanels />
      ) : (
        <>
          <DetailPanels />
          <ChildTable />
        </>
      )}
    </div>
  );
}
